/**
 * Connects event models to the dom.
 * @mdoule app/views/EventViewAdapter
 */
import LiveScoreViewProcessor from '../components/LiveScoreViewProcessor';
import WasPriceViewProcessor from '../components/WasPriceViewProcessor';

/**
 * Utility functions used in the API
 *
 * @type {Object}
 */
const utilities = {

    /**
     * Functions related to live scores
     *
     * @type {Object}
     */
    scores: {

        /**
         * Based on a scores object, returns true
         * when SB should display set score
         *
         * @param   {Object} scores Scores object
         *
         * @returns {Boolean}       Returns true or false
         *
         * @private
         */
        shouldShowSetAndScores: (scores) => scores.set && scores.leg === '',

        /**
         * Based on a scores object, returns true
         * when SB should display set and leg scores
         *
         * @param   {Object} scores Scores object
         *
         * @returns {Boolean}       Returns true or false
         *
         * @private
         */
        shouldShowSetAndLeg: (scores) => scores.set && scores.leg !== '',

        /**
         * Based on a scores object, returns true
         * when SB should display leg score
         *
         * @param   {Object} scores Scores object
         *
         * @returns {Boolean}       Returns true or false
         *
         * @private
         */
        shouldShowLegAndScores: (scores) => !scores.set && scores.leg,

        /**
         * Returns true when SB should show a default
         * message
         *
         * @param   {Object} scores Scores object
         *
         * @returns {Boolean}       Returns true or false
         *
         * @private
         */
        shouldShowDefault: (scores) => !scores.set && !scores.leg,

        /**
         * Handles render control for set scores
         *
         * @param   {Object} livescore jQuery element
         * @param   {Object} scores    Scores object
         *
         * @returns {void}
         *
         * @private
         */
        processSetAndScores: (livescore, scores) => {

            if (utilities.scores.shouldShowSetAndScores(scores)) {
                utilities.displayLivescoreFirstScore(livescore, scores.set);
                utilities.displayLivescoreComment(livescore, scores);
            }
        },

        /**
         * Handles render control for set & leg scores
         *
         * @param   {Object} livescore jQuery element
         * @param   {Object} scores    Scores object
         *
         * @returns {void}
         *
         * @private
         */
        processSetAndLeg: (livescore, scores) => {

            if (utilities.scores.shouldShowSetAndLeg(scores)) {
                utilities.displayLivescoreFirstScore(livescore, scores.set);
                utilities.displayLivescoreSecondScore(livescore, scores.leg);
            }
        },

        /**
         * Handles render control for leg scores
         *
         * @param   {Object} livescore jQuery element
         * @param   {Object} scores    Scores object
         *
         * @returns {void}
         *
         * @private
         */
        processLegAndScores: (livescore, scores) => {

            if (utilities.scores.shouldShowLegAndScores(scores)) {
                utilities.displayLivescoreFirstScore(livescore, scores.leg);
                utilities.displayLivescoreComment(livescore, scores);
            }
        },

        /**
         * Handles render control for default messages
         *
         * @param   {Object} livescore jQuery element
         * @param   {Object} scores    Scores object
         *
         * @returns {void}
         *
         * @private
         */
        processDefault: (livescore, scores) => {

            if (utilities.scores.shouldShowDefault(scores)) {
                utilities.displayLivescoreComment(livescore, scores, true);
            }
        },

        /**
         * Handle processing commentary on a single span
         *
         * @param   {Object} livescore jQuery element
         * @param   {Object} scores    Scores object
         *
         * @returns {void}
         *
         * @private
         */
        processCommentAlone: (livescore, scores) => {

            livescore.find('.area-livescore span')
                .first()
                .hide();
            livescore.find('.area-livescore span')
                .last()
                .text(LiveScoreViewProcessor.scoreTypeToText(scores.type))
                .removeClass('status')
                .addClass('event__badge');
        },

        /**
         * Handle processing commentary on both spans
         * depending on type
         *
         * @param   {Object} livescore jQuery element
         * @param   {Object} scores    Scores object
         *
         * @returns {void}
         *
         * @private
         */
        processCommentRegular: (livescore, scores) => {

            if (scores.type && scores.type !== 'GEN') {
                livescore.find('.area-livescore span')
                    .last()
                    .text(LiveScoreViewProcessor.scoreTypeToText(scores.type))
                    .removeClass('event__badge')
                    .addClass('status');
            } else {
                livescore.find('.area-livescore span')
                    .last()
                    .hide();
            }
        },
    },

    /**
     * setting the scores in the old market templates
     *
     * @param {Object} livescore jQuery element
     * @param {Object} scores data
     *
     * @returns {void}
     *
     * @private
     */
    scoresProcessing: (livescore, scores) => {

        utilities.scores.processSetAndScores(livescore, scores);
        utilities.scores.processSetAndLeg(livescore, scores);
        utilities.scores.processLegAndScores(livescore, scores);
        utilities.scores.processDefault(livescore, scores);
    },

    /**
     * Display a text in the first span of the livescore
     * container
     *
     * @param   {Object} livescore    jQuery element
     * @param   {[type]} text         Text to display
     *
     * @returns {Object}
     *
     * @private
     */
    displayLivescoreFirstScore: (livescore, text) => {

        livescore.find('.area-livescore span.in-play-scores')
            .show();

        livescore.find('.area-livescore span')
            .first()
            .text(text)
            .removeClass('status')
            .addClass('event__badge');
    },

    /**
     * Display a text in the second span of the livescore
     * container
     *
     * @param   {Object} livescore    jQuery element
     * @param   {[type]} text         Text to display
     *
     * @returns {Object}
     *
     * @private
     */
    displayLivescoreSecondScore: (livescore, text) => {

        livescore.find('.area-livescore span')
            .show();

        livescore.find('.area-livescore span')
            .last()
            .text(text)
            .removeClass('status')
            .addClass('event__badge')
            .css('display', '');
    },

    /**
     * Display a preset comment string based on scores.type
     *
     * @param   {Object} livescore    jQuery element
     * @param   {Object} scores       Scores object
     * @param   {Boolean} commentAlone Flag which determines render type
     *
     * @returns {void}
     *
     * @private
     */
    displayLivescoreComment: (livescore, scores, commentAlone) => {

        if (commentAlone) {
            utilities.scores.processCommentAlone(livescore, scores);
            return;
        }

        utilities.scores.processCommentRegular(livescore, scores);
    },
};

/**
 * Update function specific to a container
 *
 * @param   {Object} eventContainer  jQuery element
 *
 * @returns {Function} update function to be called with pre-processed livescores data.
 *
 * @private
 */
const getUpdateCb = (eventContainer) => {

    if (eventContainer.hasClass('markettable__event')) {

        //old templates using basic scores processing
        eventContainer.find('.area-livescore').show();
        return utilities.scoresProcessing.bind(null, eventContainer);
    } else {

        //event template supports sport specific display and animations we use the live score view processor
        return LiveScoreViewProcessor.setEventScores.bind(null, eventContainer);
    }
};

/**
 * Update function of was price
 *
 * @param   {Object} eventContainer  jQuery element
 *
 * @returns {Function} update function to be called with enhanced odds data.
 *
 * @private
 */
const getWasPriceUpdateCb = (eventContainer) => {
    return WasPriceViewProcessor.setWasPrice.bind(null, eventContainer);
};

/**
 * Public methods.
 *
 * @type {Object}
 *
 * @public
 */
const api = {
    /**
     * wasPrice function that returns an 'update' function
     * to be used with scores.
     *
     * @param   {String} eventId
     * @param   {Object} container  jQuery element
     *
     * @returns {Object} an object with 'update' function in it
     *                   expecting to be called with enhanced
     *                   odds data.
     *
     * @public
     */
    wasPrice: (eventId, container) => {

        if (container) {
            const eventContainer = container.find(`#${eventId}`);

            return {
                update: eventContainer.length ? getWasPriceUpdateCb(eventContainer) : () => {},
            };
        }
    },

    /**
     * Livescore function that returns an 'update' function
     * to be used with scores.
     *
     * @param   {String} eventId
     * @param   {Object} container  jQuery element
     *
     * @returns {Object} an object with 'update' function in it
     *                   expecting to be called with pre-processed
     *                   livescore data.
     *
     * @public
     */
    livescore: (eventId, container) => {

        // TODO: try and pull dom selector from WH.sportsbook.model instead?
        if (container) {
            const eventContainer = container.find(`#${eventId}`);

            return {
                update: eventContainer.length ? getUpdateCb(eventContainer) : () => {},
            };
        }
    },
};

export default api;
